import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDownToLine, Trash2, Server, Cpu, Bot, RefreshCw, AlertTriangle } from "lucide-react";
import { useNode, useNodeAgents } from "@/api/queries/nodes";
import { useDrainNode, useDeleteNode } from "@/api/mutations/nodes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { AgentMiniTable } from "@/components/AgentMiniTable";
import { relativeTime } from "@/lib/relativeTime";
import { cn } from "@/lib/utils";
import { ApiError } from "@/api/client";
import type { Node } from "@/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";

function utilTone(n: Node): { bar: string; pct: number } {
  const pct = n.capacity.maxAgents > 0 ? Math.round((n.runningAgents / n.capacity.maxAgents) * 100) : 0;
  if (n.status === "offline") return { bar: "bg-muted", pct: 0 };
  if (pct > 85) return { bar: "bg-danger", pct };
  if (pct > 65) return { bar: "bg-warn", pct };
  return { bar: "bg-ok", pct };
}

export function NodeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useNode(id);
  const agentsQuery = useNodeAgents(id);
  const drainMutation = useDrainNode();
  const deleteMutation = useDeleteNode();
  const [drainOpen, setDrainOpen] = useState(false);
  const [drainError, setDrainError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const errorMessage =
    error instanceof ApiError && error.status === 404 ? `Node "${id}" nicht gefunden` : (error as Error | undefined)?.message;

  const agents = agentsQuery.data ?? [];

  function handleDrain() {
    if (!data) return;
    setDrainError(null);
    drainMutation.mutate(data.id, {
      onSuccess: ({ drained }) => {
        toast.success(`${drained} Agent(s) gedrained`);
        setDrainOpen(false);
      },
      onError: (err) => setDrainError((err as Error).message),
    });
  }

  function handleDelete() {
    if (!data) return;
    setDeleteError(null);
    deleteMutation.mutate(data.id, {
      onSuccess: () => {
        toast.success(`Node ${data.hostname} gelöscht`);
        setDeleteOpen(false);
        navigate("/nodes");
      },
      onError: (err) => setDeleteError((err as Error).message),
    });
  }

  // ----- Loading / Error -----
  if (isLoading && !data) {
    return (
      <SectionCard testId="detail-loading">
        <div className="flex items-center gap-3 py-8 text-sm text-text3">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Node…
        </div>
      </SectionCard>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-5">
        <BackLink to="/nodes" label="Nodes" />
        <SectionCard testId="detail-error">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-danger" />
            <div className="text-sm text-text2">{errorMessage ?? "Konnte nicht laden"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4" />Retry</Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  const { bar, pct } = utilTone(data);
  const tiles: Array<[string, string]> = [
    ["CPU", `${data.capacity.cpu} vCPU`],
    ["RAM", `${data.capacity.memoryGb} GB`],
    ["Backend", data.backends.join(", ") || "—"],
  ];

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo="/nodes"
        backLabel="Nodes"
        icon={Server}
        title={data.hostname}
        mono
        badge={
          <Badge variant={data.status === "online" ? "online" : "offline"}>
            {data.status === "online" && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            {data.status}
          </Badge>
        }
        actions={
          <div className="flex flex-wrap gap-2" data-testid="node-actions">
            <Button variant="outline" size="sm" onClick={() => setDrainOpen(true)} disabled={data.runningAgents === 0} data-testid="node-action-drain">
              <ArrowDownToLine className="h-4 w-4" />Drain ({data.runningAgents})
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="node-action-delete">
              <Trash2 className="h-4 w-4" />Delete
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-[18px]">
          {/* Kapazität & Auslastung */}
          <SectionCard icon={<Cpu className="h-[18px] w-[18px]" />} title="Kapazität & Auslastung">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[12.5px] font-semibold text-text2">Agents</span>
              <span className="tnum text-[12.5px] text-text3">
                {data.status === "offline" ? "offline" : `${data.runningAgents} / ${data.capacity.maxAgents}`}
              </span>
            </div>
            <div className="mb-[18px] h-[9px] overflow-hidden rounded-full bg-surface3">
              <div className={cn("h-full rounded-full transition-all", bar)} style={{ width: `${pct}%` }} />
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              {tiles.map(([label, value]) => (
                <div key={label} className="rounded-lg bg-surface2 px-3 py-2.5">
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-text3">{label}</div>
                  <div className="mono mt-0.5 truncate text-[14px] font-semibold text-text" title={value}>{value}</div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Zugewiesene Agents */}
          <SectionCard
            testId="node-agents-section"
            icon={<Bot className="h-[18px] w-[18px]" />}
            title={`Zugewiesene Agents (${agents.length})`}
            description="Laufen aktuell auf diesem Node. Bei Drain werden alle weggenommen."
            noPad
          >
            <div className="px-[18px] py-2">
              {agentsQuery.isError ? (
                <p className="py-3 text-sm text-danger" data-testid="node-agents-error">
                  Agents konnten nicht geladen werden — Anzeige evtl. unvollständig. Seite neu laden.
                </p>
              ) : (
                <AgentMiniTable agents={agents} emptyText="Keine Agents auf diesem Node." />
              )}
            </div>
          </SectionCard>
        </div>

        <PropGrid
          rows={[
            ["ID", <span className="mono">{data.id}</span>],
            ["Hostname", <span className="mono">{data.hostname}</span>],
            ["Status", <Badge variant={data.status === "online" ? "online" : "offline"}>{data.status}</Badge>],
            ["Backends", data.backends.join(", ") || "—"],
            ["Running / Max", <span className="tnum">{data.runningAgents} / {data.capacity.maxAgents}</span>],
            ["CPU", `${data.capacity.cpu} cores`],
            ["Memory", `${data.capacity.memoryGb} GB`],
            ["Erstellt", <span className="text-text2">{relativeTime(data.createdAt)}</span>],
            ["Letzte Aktivität", <span className="text-text2">{relativeTime(data.lastSeen)}</span>],
          ]}
        />
      </div>

      <ConfirmDialog
        open={drainOpen}
        onOpenChange={setDrainOpen}
        title={`Node ${data.hostname} draine`}
        description={`Alle ${agents.length} Agent(s) werden vom Node weggenommen (Brain-Container werden gestoppt). Agents bleiben in der DB und können neu zugewiesen werden.`}
        confirmText="Drain ausführen"
        confirmVariant="destructive"
        isPending={drainMutation.isPending}
        errorMessage={drainError ?? undefined}
        onConfirm={handleDrain}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Node endgültig löschen"
        description="Diese Aktion ist NICHT umkehrbar. Blockt wenn noch Agents assigned sind — vorher draine."
        confirmText="Endgültig löschen"
        confirmVariant="destructive"
        isPending={deleteMutation.isPending}
        errorMessage={deleteError ?? undefined}
        requiredTypeText={data.hostname}
        onConfirm={handleDelete}
      />
    </div>
  );
}
